import React from 'react'
import { useSelector } from 'react-redux'
import { selectors as ChannelsSelectors } from 'data/channels'
import { selectors as ConfigSelectors } from 'data/config'
import { currentUserCanManageChannel } from 'data/rights/selectors'

import { Text } from 'components/base'

import ModalTabs, { ModalTab } from 'components/Utils/Modal/Tabs'
import ChannelsSettingsGeneral from './General'
import ChannelsSettingsSharing from './Sharing'
import ChannelsSettingsMemberships from './Memberships'
import IconButton from 'components/Utils/IconButton'
import {
  CogIcon,
  LinkIcon,
  PeopleIcon,
  CloseIcon,
  ShareIcon,
} from 'components/Utils/Icons'

import { providesChannelMemberships } from '../dataSync/channelMembers'

export type ChannelsSettingsProps = {
  channelId: string
  closeModal: () => void
  defaultTabId: string
}

export default providesChannelMemberships(function ChannelsSettings({
  channelId,
  closeModal,
  defaultTabId,
}: ChannelsSettingsProps) {
  const [currentTabId, setCurrentTabId] = React.useState(defaultTabId || 'general')

  const channel = useSelector(state =>
    ChannelsSelectors.getChannel(state, channelId)
  )
  const useMergedShareAndPermissionsTab = useSelector(state =>
    ConfigSelectors.hasFlag(state, 'merge-share-and-permissions')
  )
  const canManage = useSelector(state =>
    currentUserCanManageChannel(state, channel)
  )

  if (!channel) {
    return null
  }

  return (
    <ModalTabs
      style={{ minWidth: '160px' }}
      currentTabId={currentTabId}
      setCurrentTabId={setCurrentTabId}
      renderHeader={() => (
        <div className="flexbox flexbox-space-between flexbox-align-center">
          <Text.Title>{getHeaderTitle(currentTabId)}</Text.Title>
          <IconButton onClick={closeModal}>
            <CloseIcon size={16} />
          </IconButton>
        </div>
      )}
    >
      <ModalTab tabId="general" tabLabel={'General'} tabIcon={CogIcon}>
        <ChannelsSettingsGeneral channel={channel} canManage={canManage} />
      </ModalTab>
      {!useMergedShareAndPermissionsTab && (
        <ModalTab tabId="users" tabLabel="Permissions" tabIcon={PeopleIcon}>
          <ChannelsSettingsMemberships channelId={channel.id} />
        </ModalTab>
      )}
      <ModalTab
        tabId="sharing"
        tabLabel={useMergedShareAndPermissionsTab ? 'Share' : 'Sharing'}
        tabIcon={useMergedShareAndPermissionsTab ? ShareIcon : LinkIcon}
      >
        <ChannelsSettingsSharing channel={channel} canManage={canManage} />
      </ModalTab>
    </ModalTabs>
  )
})

function getHeaderTitle(id: string): string {
  switch (id) {
    case 'general':
      return 'Channel settings'
    case 'users':
      return 'Manage channel permissions'
    case 'sharing':
      return 'Share'
    default:
      return 'Unknown'
  }
}
